/**
 * Vendor Balances — "Vendor Balances" tab (Accounting Session 3).
 * RECONSTRUCTED/HISTORICAL data only — deliberately separate from the
 * live "Vendor Ledger" tab (trip.costItems). Backend: one small new
 * aggregation endpoint (GET /api/historical-records/vendor-balances[/detail])
 * added this session; everything else is pure UI.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Landmark, ArrowUpDown, Eye, Loader2, ShieldCheck } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;
const fmtDate = (d) => (d ? String(d).slice(0, 10) : '—');
const recordAmount = (fields) => {
  const entry = fields?.amount || fields?.Amount || fields?.netPayable || fields?.Ref || fields?.['Net Payable'];
  return entry ? entry.value : 0;
};

const VendorDetailSheet = ({ vendor, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(`${BACKEND_URL}/api/historical-records/vendor-balances/detail`, { params: { vendor } })
      .then((r) => setData(r.data))
      .catch(() => toast.error('Failed to load vendor detail'))
      .finally(() => setLoading(false));
  }, [vendor]);

  return (
    <Sheet open onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto" data-testid="vendor-detail-sheet">
        <SheetHeader>
          <SheetTitle>{vendor}</SheetTitle>
          <SheetDescription>Reconstructed historical transactions for this vendor only — never live Vendor Ledger data.</SheetDescription>
        </SheetHeader>
        {loading || !data ? (
          <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 text-teal-600 animate-spin" /></div>
        ) : (
          <div className="space-y-4 mt-4">
            <Card className="p-4 grid grid-cols-3 gap-3 text-center" data-testid="vendor-detail-totals">
              <div><div className="text-[11px] text-slate-400 uppercase">Purchases</div><div className="text-sm font-semibold text-slate-700">{inr(data.totalPurchases)}</div></div>
              <div><div className="text-[11px] text-slate-400 uppercase">Payments</div><div className="text-sm font-semibold text-slate-700">{inr(data.totalPayments)}</div></div>
              <div><div className="text-[11px] text-slate-400 uppercase">Balance</div><div className="text-sm font-bold text-teal-700" data-testid="vendor-detail-balance">{inr(data.balance)}</div></div>
            </Card>
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{data.recordCount} transaction(s)</div>
            <div className="space-y-2" data-testid="vendor-detail-records">
              {(data.records || []).map((r) => (
                <Card key={r.historicalId} className="p-3 flex items-center justify-between gap-3" data-testid={`vendor-record-${r.historicalId}`}>
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-800 truncate">{r.crmDisplayNumber}</div>
                    <div className="text-[11px] text-slate-400">{r.originalNumber || '—'} · {fmtDate(r.originalDate)}</div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Badge className="bg-slate-100 text-slate-600 text-[10px] uppercase">{r.recordType}</Badge>
                    <span className="text-sm font-medium text-slate-700">
                      {inr(recordAmount(r.fields))}
                    </span>
                  </div>
                </Card>
              ))}
              {data.records.length === 0 && <p className="text-xs text-slate-400">No transactions found for this vendor.</p>}
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

const AccountingVendorBalances = () => {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('balance');
  const [selectedVendor, setSelectedVendor] = useState(null);

  const load = () => {
    setLoading(true);
    axios.get(`${BACKEND_URL}/api/historical-records/vendor-balances`)
      .then((r) => setVendors(r.data?.vendors || []))
      .catch(() => toast.error('Failed to load vendor balances'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const sorted = [...vendors]
    .filter((v) => v.vendor.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => (sortBy === 'vendor' ? a.vendor.localeCompare(b.vendor) : b[sortBy] - a[sortBy]));

  const cycleSort = () => setSortBy((s) => (s === 'balance' ? 'recordCount' : s === 'recordCount' ? 'vendor' : 'balance'));

  return (
    <div className="space-y-4" data-testid="accounting-vendor-balances-tab">
      <div>
        <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-600 flex items-center gap-2">
          <Landmark className="h-4 w-4 text-teal-700" />Vendor Balances (Reconstructed)
        </h2>
        <p className="text-xs text-slate-400 mt-0.5">
          Historical purchase/payment/vendor-transaction records grouped by vendor — separate from the live "Vendor Ledger" tab, never merged with it.
        </p>
      </div>

      <Card className="p-3 flex items-center gap-2 flex-wrap" data-testid="vendor-balances-filter-bar">
        <Input placeholder="Search vendor…" className="w-56 h-8 text-xs" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="vendor-search-input" />
        <Button size="sm" variant="outline" onClick={cycleSort} data-testid="vendor-sort-btn">
          <ArrowUpDown className="h-3.5 w-3.5 mr-1" />Sort: {sortBy === 'balance' ? 'Balance' : sortBy === 'recordCount' ? 'Transactions' : 'Vendor name'}
        </Button>
        <Button size="sm" variant="outline" onClick={load} data-testid="vendor-refresh-btn">Refresh</Button>
      </Card>

      {loading ? (
        <div className="flex justify-center py-16" data-testid="vendor-balances-loading"><Loader2 className="h-7 w-7 text-teal-600 animate-spin" /></div>
      ) : sorted.length === 0 ? (
        <Card className="p-12 text-center" data-testid="vendor-balances-empty-state">
          <ShieldCheck className="h-10 w-10 text-slate-200 mx-auto mb-3" />
          <p className="text-slate-500">No reconstructed vendor transactions found.</p>
        </Card>
      ) : (
        <div className="space-y-2" data-testid="vendor-balances-list">
          {sorted.map((v) => (
            <Card key={v.vendor} className="p-3 flex items-center justify-between gap-3 flex-wrap" data-testid={`vendor-row-${v.vendor}`}>
              <div className="min-w-0">
                <div className="text-sm font-medium text-slate-800 truncate">{v.vendor}</div>
                <div className="text-[11px] text-slate-400">{v.recordCount} transaction(s) · Purchases {inr(v.totalPurchases)} · Payments {inr(v.totalPayments)}</div>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <span className="text-sm font-bold text-teal-700" data-testid={`vendor-balance-${v.vendor}`}>{inr(v.balance)}</span>
                <Button size="sm" variant="outline" onClick={() => setSelectedVendor(v.vendor)} data-testid={`view-vendor-${v.vendor}`}>
                  <Eye className="h-3.5 w-3.5 mr-1" />Detail
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {selectedVendor && <VendorDetailSheet vendor={selectedVendor} onClose={() => setSelectedVendor(null)} />}
    </div>
  );
};

export default AccountingVendorBalances;
